import { ReactNode, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/hooks/useOrganization';
import { Loader2 } from 'lucide-react';

interface AdminRouteProps {
  children: ReactNode;
}

/**
 * Protected route wrapper for admin pages.
 * Requires user to be authenticated AND have internal_admin role.
 * Redirects unauthenticated users to /auth and non-admin users to /.
 */
export function AdminRoute({ children }: AdminRouteProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isAdmin, loading: orgLoading } = useOrganization();

  // Debug logging (temporary)
  console.log('[AdminRoute] State:', { authLoading, orgLoading, isAuthenticated, isAdmin });

  // Inject noindex meta tag for admin routes (SEO: prevent indexing admin pages)
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"][data-admin-route]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'robots';
      meta.setAttribute('data-admin-route', 'true');
      document.head.appendChild(meta);
    }
    meta.content = 'noindex, nofollow';

    return () => {
      const tag = document.querySelector('meta[name="robots"][data-admin-route]');
      if (tag) tag.remove();
    };
  }, []);

  // Show loading state while checking auth and role
  if (authLoading || orgLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Redirect to auth if not authenticated
  if (!isAuthenticated) {
    console.log('[AdminRoute] Not authenticated, redirecting to /auth');
    return <Navigate to="/auth" replace />;
  }

  // Redirect to home if not an admin
  if (!isAdmin) {
    console.log('[AdminRoute] Not admin, redirecting to /');
    return <Navigate to="/" replace />;
  }

  console.log('[AdminRoute] Access granted, rendering admin content');
  return <>{children}</>;
}
